import React, { useEffect, useMemo, useState } from "react";
import { CaretUp, CaretDown, ArrowCounterClockwise, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Universal DataTable — sortable (type-aware, shift+click multi-column), filterable,
 * state persisted per table per user (localStorage), with reset-to-default.
 *
 * columns: [{ key, label, type: "text"|"number"|"date"|"money"|"status", render?, sortable=true }]
 */
const load = (id) => {
  try { return JSON.parse(localStorage.getItem(`dt_${id}`)) || {}; } catch { return {}; }
};
const save = (id, state) => {
  try { localStorage.setItem(`dt_${id}`, JSON.stringify(state)); } catch {}
};

const cmp = (a, b, type) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (type === "number" || type === "money") return Number(a) - Number(b);
  if (type === "date") return String(a).localeCompare(String(b));
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
};

export const DataTable = ({ tableId, columns, rows, defaultSort = [], searchKeys = null, emptyText = "Nothing here yet.", rowTestId }) => {
  const persisted = useMemo(() => load(tableId), [tableId]);
  const [sorts, setSorts] = useState(persisted.sorts || defaultSort); // [{key, dir}]
  const [search, setSearch] = useState(persisted.search || "");

  useEffect(() => { save(tableId, { sorts, search }); }, [tableId, sorts, search]);

  const clickSort = (key, shift) => {
    setSorts(prev => {
      const existing = prev.find(s => s.key === key);
      const next = existing
        ? existing.dir === "asc"
          ? prev.map(s => s.key === key ? { ...s, dir: "desc" } : s)
          : prev.filter(s => s.key !== key) // third click = remove
        : shift ? [...prev, { key, dir: "asc" }] : [{ key, dir: "asc" }];
      return shift ? next : next.filter(s => s.key === key);
    });
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    const keys = searchKeys || columns.map(c => c.key);
    return rows.filter(r => keys.some(k => String(r[k] ?? "").toLowerCase().includes(q)));
  }, [rows, search, columns, searchKeys]);

  const sorted = useMemo(() => {
    if (!sorts.length) return filtered;
    const typeOf = Object.fromEntries(columns.map(c => [c.key, c.type || "text"]));
    return [...filtered].sort((a, b) => {
      for (const s of sorts) {
        const d = cmp(a[s.key], b[s.key], typeOf[s.key]) * (s.dir === "asc" ? 1 : -1);
        if (d !== 0) return d;
      }
      return 0;
    });
  }, [filtered, sorts, columns]);

  const reset = () => { setSorts(defaultSort); setSearch(""); };

  return (
    <div data-testid={`datatable-${tableId}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#000000]" />
          <input data-testid={`datatable-${tableId}-search`} value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Filter…" className="rounded-full border border-[#C4C8CE] pl-8 pr-3 py-1.5 text-xs w-52" />
        </div>
        <span className="text-[10px] text-[#000000]">{sorted.length} row{sorted.length === 1 ? "" : "s"}{sorts.length > 1 ? " · multi-sort" : ""}</span>
        <button data-testid={`datatable-${tableId}-reset`} onClick={reset}
          className="btn-ghost text-[10px] flex items-center gap-1 text-[#000000] ml-auto" title="Reset sorting & filters">
          <ArrowCounterClockwise size={11} /> Reset to default
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-[#000000] border-b border-[#EDEEF0]">
              {columns.map(c => {
                const s = sorts.find(x => x.key === c.key);
                const idx = sorts.findIndex(x => x.key === c.key);
                return (
                  <th key={c.key} className={`py-1.5 pr-3 ${c.sortable === false ? "" : "cursor-pointer select-none hover:text-[#000000]"}`}
                    data-testid={`datatable-${tableId}-th-${c.key}`}
                    onClick={(e) => c.sortable !== false && clickSort(c.key, e.shiftKey)}
                    title={c.sortable === false ? undefined : "Click to sort · Shift+click for secondary sort"}>
                    <span className="inline-flex items-center gap-0.5">
                      {c.label}
                      {s && (s.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
                      {s && sorts.length > 1 && <sup className="text-[8px]">{idx + 1}</sup>}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={columns.length} className="py-6 text-center text-xs text-[#000000]">{emptyText}</td></tr>
            ) : sorted.map((r, i) => (
              <tr key={r.id || i} className="border-b border-[#F4F4F5] hover:bg-[#FAFAFA]"
                data-testid={rowTestId ? rowTestId(r) : undefined}>
                {columns.map(c => (
                  <td key={c.key} className="py-2 pr-3 align-top">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
